import { apiClient } from "@/services/api.client"

interface UploadIntent {
  mode: "proxy" | "s3"
  upload_url?: string
  fields?: Record<string, string>
  upload_token?: string
  expires_in?: number
}

interface BlobUploadResponse {
  blob_url: string
}

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
}

function uploadContentType(file: File): string {
  if (file.type) return file.type
  const extension = file.name.split(".").pop()?.toLowerCase() ?? ""
  return MIME_BY_EXTENSION[extension] ?? "application/octet-stream"
}

/**
 * Upload an OIO/OIA case document. AWS uses an authenticated upload intent,
 * a browser S3 form POST to its short-lived URL, then an authenticated size
 * check. GCP keeps the existing multipart proxy upload contract.
 */
export async function uploadCaseDocument(file: File, legacyUserId?: string): Promise<string> {
  const intent = await apiClient.post<UploadIntent>("/api/v1/storage/upload-intent", {
    filename: file.name,
    content_type: uploadContentType(file),
    size: file.size,
  })

  if (intent.mode === "s3") {
    if (!intent.upload_url || !intent.upload_token) throw new Error("The upload service returned an incomplete upload URL.")
    const form = new FormData()
    for (const [name, value] of Object.entries(intent.fields ?? {})) form.append(name, value)
    form.append("file", file)
    const uploaded = await fetch(intent.upload_url, { method: "POST", body: form })
    if (!uploaded.ok) throw new Error(`The upload of ${file.name} failed (${uploaded.status}).`)
    const completed = await apiClient.post<BlobUploadResponse>("/api/v1/storage/upload-complete", {
      upload_token: intent.upload_token,
    })
    if (!completed?.blob_url) throw new Error(`The upload of ${file.name} could not be confirmed.`)
    return completed.blob_url
  }

  const form = new FormData()
  form.append("file", file)
  if (legacyUserId) form.append("user_id", legacyUserId)
  const result = await apiClient.upload<BlobUploadResponse>("/api/v1/storage/upload", form)
  if (!result?.blob_url) throw new Error(`The upload of ${file.name} returned no storage address.`)
  return result.blob_url
}
